import * as turf from '@turf/turf';

import { MAPBOX_SOURCECACHE_LAYER_PREFIX } from '../../../constants/map';
import {
  getCalculateSuitabilityFromMap,
  getClippableTilesetLayers,
} from './helpers';

/**
 * Calculates suitability across multiple layers in the map. It returns a
 * mapping of layerIds to the number of suitable acres for each layer.
 *
 * It's a similar to `colorForLayerPoint`, but it calculates suitability across
 * many points on the map and across many layers (instead of just one point on
 * one layer).
 *
 * WARNING: this function uses internal Mapbox GL JS APIs.
 */
const suitabilityAcrossLayers = (map, layerIds, featureCollection) => {
  /* eslint-disable no-underscore-dangle */
  if (!map || !featureCollection?.features?.length || !layerIds?.length) {
    throw new Error('Invalid arguments');
  }

  const layers = getClippableTilesetLayers(map, layerIds);

  // Store init values so we can restore them at the end:
  const originalPosition = {
    center: map.getCenter(),
    zoom: map.getZoom(),
    pitch: map.getPitch(),
  };
  const originalUniformValues = Object.fromEntries(
    layers.map((layer) => [
      layer.implementation.id,
      {
        opacity: layer.implementation.opacity,
        fadeFactor: layer.implementation.fadeFactor,
      },
    ]),
  );

  // Adjust map center/zoom to fit the bounds of the featureCollection
  const bounds = turf.bbox(featureCollection);
  if (!bounds) {
    console.error("couldn't get bounds for featureCollection");
    return null;
  }
  map.fitBounds(bounds, { padding: 10, animate: false }); // minimal padding needed for this

  // Get a fn that will calculate suitability from the map within `featureCollection`
  // NOTE: we call this here so that we only generate the points to sample once
  const calculateSuitabilityFromMap = getCalculateSuitabilityFromMap({
    map,
    featureCollection,
  });

  const getSuitabilityForLayer = (layer) => {
    const { painter } = map;
    const customLayer = layer.implementation;

    // Adjust the layer's settings so the layer is fully opaque and the filtered out colors are fully transparent
    customLayer.updateOpacity(1);
    customLayer.updateFadeFactor(1);

    // Clear the painter so we can just draw this one layer
    painter.context.viewport.set([0, 0, painter.width, painter.height]);
    painter.context.clear({
      color: 'rgba(0,0,0,0)',
      depth: 1,
    });
    painter.clearStencil();

    // Render the layer
    const sourceCacheKey = `${MAPBOX_SOURCECACHE_LAYER_PREFIX}${customLayer.source}`;
    const sourceCache = painter.style._sourceCaches[sourceCacheKey];
    if (!sourceCache) {
      console.error('no source cache found!');
      return null;
    }
    const coords = sourceCache.getVisibleCoordinates().slice().reverse();
    painter.renderPass = 'translucent';
    painter.renderLayer(painter, sourceCache, layer, coords);

    return calculateSuitabilityFromMap();
  };

  try {
    const suitabilityByLayerId = {};
    layers.forEach((layer) => {
      suitabilityByLayerId[layer.id] = getSuitabilityForLayer(layer);
    });

    return suitabilityByLayerId;
  } catch (err) {
    console.error(err);
    return null;
  } finally {
    // Reset the map position
    map.setCenter(originalPosition.center, { animate: false });
    map.setZoom(originalPosition.zoom, { animate: false });
    map.setPitch(originalPosition.pitch, { animate: false });

    // Reset the opacities
    layers.forEach((layer) => {
      const { opacity, fadeFactor } = originalUniformValues[layer.id];
      layer.implementation.updateOpacity(opacity);
      layer.implementation.updateFadeFactor(fadeFactor);
    });

    // Repaint the map so the user doesn't see our render
    map.triggerRepaint();
  }
  /* eslint-enable no-underscore-dangle */
};

export default suitabilityAcrossLayers;
